import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from './i18n';
import { authFetch, isAuthenticated } from './auth';
import FoodSheetForm from './FoodSheetForm';
import FoodSheetItem from './FoodSheetItem';
import { matchKey } from './codes';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

// "Food sheets" entry of the Catalogue: archive of every food sheet the user can see, and the form to add one.
// Props (optional): focusSheet = one food sheet opened from a link (shown alone), onShowAll() = leave the single-sheet view.
function FoodSheetCatalog({ focusSheet = null, onShowAll }) {
  const { t } = useI18n();
  const [mode, setMode] = useState('archive');            // archive | new | edit
  const [sheets, setSheets] = useState(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [meId, setMeId] = useState(null);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/food-sheets`);
      setSheets(res && res.ok ? await res.json() : []);
    } catch { setSheets([]); }
  }, []);

  useEffect(() => {
    load();
    if (isAuthenticated()) {
      authFetch(`${API_URL}/me`).then((r) => (r && r.ok ? r.json() : null)).then((u) => { if (u) setMeId(u.id); }).catch(() => {});
    }
  }, [load]);

  const remove = async (sheet) => {
    if (!window.confirm(t('catalog.food.confirm_delete'))) return;
    try {
      const res = await authFetch(`${API_URL}/food-sheets/${sheet.id}`, { method: 'DELETE' });
      if (!res || !res.ok) throw new Error();
      setNote({ type: 'success', text: t('catalog.food.deleted') });
      load();
    } catch {
      setNote({ type: 'error', text: t('catalog.food.delete_error') });
    }
  };

  const done = (text) => {
    setMode('archive'); setEditing(null); setNote(text ? { type: 'success', text } : null); load();
    if (focusSheet && onShowAll) onShowAll();          // the single-sheet view would show the old data after an edit
  };

  if (mode === 'new') {
    return <div className="catalog"><FoodSheetForm onCancel={() => setMode('archive')} onDone={() => done()} /></div>;
  }
  if (mode === 'edit' && editing) {
    return <div className="catalog"><FoodSheetForm initialData={editing} onCancel={() => done()} onDone={() => done(t('catalog.food.updated'))} /></div>;
  }

  if (focusSheet) {
    return (
      <div className="catalog">
        <h2>{t('catalog.foodsheets.title')}</h2>
        <p className="import-hint">{t('app.link.food_single')}{' '}
          <button type="button" className="chip" onClick={() => onShowAll && onShowAll()}>{t('app.link.show_all_food')}</button></p>
        {note && <div className={`message ${note.type}`}>{note.text}</div>}
        <ul className="sheet-list">
          <FoodSheetItem sheet={focusSheet} meId={meId} heading={<strong>{focusSheet.dish_name}</strong>}
            onEdit={(s) => { setEditing(s); setMode('edit'); }}
            onDelete={async (s) => { await remove(s); if (onShowAll) onShowAll(); }} />
        </ul>
      </div>
    );
  }

  const key = matchKey(query);
  const shown = (sheets || []).filter((s) => !key || matchKey(s.dish_name || '').includes(key));
  return (
    <div className="catalog">
      <h2>{t('catalog.foodsheets.title')}</h2>
      <div className="catalog-search">
        <input type="search" placeholder={t('catalog.foodsheets.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <p><button type="button" className="submit-btn" onClick={() => setMode('new')}>{t('app.nav.new_food')}</button></p>
      {note && <div className={`message ${note.type}`}>{note.text}</div>}
      {sheets === null && <p className="empty">{t('ui.loading')}</p>}
      {sheets !== null && shown.length === 0 && <p className="empty">{t('catalog.foodsheets.none')}</p>}
      {shown.length > 0 && (
        <>
          <h3>{t('app.nav.archive', { n: shown.length })}</h3>
          <ul className="sheet-list">
            {shown.map((sheet) => (
              <FoodSheetItem key={sheet.id} sheet={sheet} meId={meId}
                heading={<strong>{sheet.dish_name}</strong>}
                onEdit={(s) => { setEditing(s); setMode('edit'); }} onDelete={remove} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default FoodSheetCatalog;
